import React, { useEffect, useState } from 'react';
import { Smartphone, Apple, Download as DownloadIcon } from 'lucide-react';
import { detectDevice, getAppStoreUrl, type DeviceInfo } from '../utils/deviceDetection';
import { analyticsService } from '../services/analytics.service';
import { useLanguage } from '../contexts/LanguageContext';

const Download = () => {
  const { t } = useLanguage();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [autoRedirect, setAutoRedirect] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const info = detectDevice();
    setDeviceInfo(info);

    if (info.isIOS || info.isAndroid) {
      setAutoRedirect(true);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = getAppStoreUrl(info.platform);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, []);

  const trackDownload = async (platform: string) => {
    try {
      await analyticsService.trackDownload(platform);
    } catch (error) {
      console.error('Analytics error:', error);
    }
  };

  const handleDownload = (platform: 'ios' | 'android') => {
    trackDownload(platform);
    const url = getAppStoreUrl(platform);
    if (url) {
      window.location.href = url;
    }
  };

  if (!deviceInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-violet-50">
        <div className="animate-pulse text-purple-600 text-xl">{t.download.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50">
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-2xl mb-6">
              <DownloadIcon className="w-10 h-10 text-purple-600" />
            </div>
            <h1 className="text-5xl font-bold mb-6 text-gray-900">
              {t.download.title}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {t.download.subtitle}
            </p>
          </div>

          {autoRedirect && countdown > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6 mb-8 text-center shadow-sm">
              <p className="text-lg mb-2 text-gray-900">
                {t.download.redirecting} {deviceInfo.isIOS ? t.download.appStore : t.download.googlePlay} {t.download.in}{' '}
                <span className="font-bold text-purple-600">{countdown}</span> {t.download.seconds}
              </p>
              <p className="text-sm text-gray-600">
                {t.download.notRedirected}
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div
              className={`bg-white border-2 ${
                deviceInfo.isIOS ? 'border-purple-500 ring-4 ring-purple-100' : 'border-gray-200'
              } rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer group`}
              onClick={() => handleDownload('ios')}
            >
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl mb-6 group-hover:scale-110 transition-transform">
                <Apple className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">{t.download.iosTitle}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{t.download.iosDescription}</p>
              <img
                src="/appstore.webp"
                alt={t.download.downloadAppStore}
                className="w-full cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload('ios');
                }}
              />
              {deviceInfo.isIOS && (
                <span className="inline-block mt-4 px-4 py-2 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                  {t.download.yourDevice}
                </span>
              )}
            </div>

            <div
              className={`bg-white border-2 ${
                deviceInfo.isAndroid ? 'border-lime-500 ring-4 ring-lime-100' : 'border-gray-200'
              } rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer group`}
              onClick={() => handleDownload('android')}
            >
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-lime-500 to-lime-600 rounded-xl mb-6 group-hover:scale-110 transition-transform">
                <Smartphone className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">{t.download.androidTitle}</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">{t.download.androidDescription}</p>
              <img
                src="/google-play-badge-logo-png-transparent-e1649165111678.webp"
                alt={t.download.downloadGooglePlay}
                className="w-full cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload('android');
                }}
              />
              {deviceInfo.isAndroid && (
                <span className="inline-block mt-4 px-4 py-2 bg-lime-100 text-lime-700 text-sm font-medium rounded-full">
                  {t.download.yourDevice}
                </span>
              )}
            </div>
          </div>

          {deviceInfo.isDesktop && (
            <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm">
              <h3 className="text-2xl font-bold mb-3 text-gray-900">{t.download.scanQR}</h3>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {t.download.scanDescription}
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-gray-50 p-8 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="w-48 h-48 mx-auto bg-white rounded-lg flex items-center justify-center mb-4 p-4 border-2 border-gray-200">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://apps.apple.com/nl/app/heitje/id6755081441')}`}
                      alt={t.download.iosQR}
                      className="w-full h-full"
                    />
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">{t.download.forIPhone}</p>
                </div>
                <div className="bg-gray-50 p-8 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="w-48 h-48 mx-auto bg-white rounded-lg flex items-center justify-center mb-4 p-4 border-2 border-gray-200">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent('https://play.google.com/store/apps/details?id=com.heitje.app')}`}
                      alt={t.download.androidQR}
                      className="w-full h-full"
                    />
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">{t.download.forAndroid}</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-12 text-center">
            <p className="text-gray-600 text-sm">
              {t.download.agreement}{' '}
              <a href="/privacy" className="text-purple-600 hover:text-purple-700 underline font-medium">
                {t.download.privacyPolicy}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Download;
